import { useTranslation } from 'react-i18next';
import { OrderLineItem } from '../../types/LineItems';
import { ReactComponent as ClaimIcon } from '../../assets/icons/claim.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';

import styles from './OrderLineItems.module.scss';
import { useContext } from 'react';
import Button from '../Button/Button';
import { ComplainContext } from '../../contexts/ComplainContext';
import { getProperty } from '../../utils/getProperty';
import { formatPrice } from '../../utils/formatPrice';
import Card from '../Card/Card';
import CardLine from '../CardLine/CardLine';
import { UserContext } from '../../contexts/UserContext';

interface Props {
   currency: string;
   lineItems: OrderLineItem[];
   selectable?: boolean;
}

export default function OrderLineItems({
   lineItems,
   selectable,
   currency,
}: Props) {
   const {
      selectedOrderLines,
      toggleSelectedOrderLine,
      setSelectedOrderLines,
   } = useContext(ComplainContext);

   const { loadingLineItems } = useContext(UserContext);

   const { t } = useTranslation('orders');

   const allIds = lineItems.map((item) => item.id);

   const handleSelectAll = () => {
      if (selectedOrderLines.length === allIds.length) {
         setSelectedOrderLines([]);
         return;
      }

      setSelectedOrderLines(allIds);
   };

   return (
      <Card
         title={t('elements')}
         loading={loadingLineItems}
         action={
            selectable
               ? {
                    label: t(
                       selectedOrderLines.length === allIds.length
                          ? 'deselect_all'
                          : 'select_all'
                    ),
                    onClick: handleSelectAll,
                 }
               : undefined
         }
      >
         {lineItems.map((orderLineItem) => (
            <CardLine key={orderLineItem.id}>
               <div key={orderLineItem.id}>
                  <div className={styles.headerWrapper}>
                     <div className={styles.header}>
                        <strong className={styles.name}>
                           {orderLineItem.name}
                        </strong>
                        <strong>
                           {orderLineItem.quantity
                              ? orderLineItem.quantity
                              : '-'}{' '}
                           {t('amount')}
                        </strong>

                        <div className={styles.prices}>
                           {orderLineItem.priceNonDiscounted !==
                              orderLineItem.price && (
                              <div className={styles.discounted}>
                                 {formatPrice(
                                    orderLineItem.priceNonDiscounted,
                                    currency
                                 )}
                              </div>
                           )}
                           <div className={styles.price}>
                              {formatPrice(orderLineItem.price, currency)}
                           </div>
                        </div>
                     </div>

                     {selectable && (
                        <Button
                           variant={
                              selectedOrderLines.includes(orderLineItem.id)
                                 ? 'grey'
                                 : 'secondary'
                           }
                           small
                           onClick={() =>
                              toggleSelectedOrderLine(orderLineItem.id)
                           }
                        >
                           {t(
                              selectedOrderLines.includes(orderLineItem.id)
                                 ? 'complain_cancel'
                                 : 'complain_element'
                           )}

                           {selectedOrderLines.includes(orderLineItem.id) ? (
                              <CloseIcon />
                           ) : (
                              <ClaimIcon />
                           )}
                        </Button>
                     )}
                  </div>

                  <div>
                     {t('width')}:{' '}
                     {
                        getProperty(
                           'width',
                           orderLineItem.variousProductProperties
                        )?.valueLocalized
                     }
                     {' • '}
                     {t('height')}:{' '}
                     {
                        getProperty(
                           'height',
                           orderLineItem.variousProductProperties
                        )?.valueLocalized
                     }
                     {' • '}
                     {t('sku')}: {orderLineItem.sku}
                  </div>
               </div>
            </CardLine>
         ))}
      </Card>
   );
}
